import { FC, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import { Download } from "@mui/icons-material";
import { tokens } from "@/tokens";
import type { IeepaAnalysisEntry } from "@/types/ieepa";
import { hasFiledClaim, refundableAmount } from "./useCapeAnalytics";

export interface ExportOptions {
  /** Include entries that already have a protest on ACE. Default off. */
  includeProtests: boolean;
  /** Include PROTEST_RECOMMENDED + OUT_OF_WINDOW buckets. Default off. */
  includeOutOfWindow: boolean;
  /** Include entries with Reconciliation Indicator = Y. Default on. */
  includeRecon: boolean;
}

interface ExportCapeReportDialogProps {
  open: boolean;
  onClose: () => void;
  entries: IeepaAnalysisEntry[];
  /** Fired when the user clicks Download. Caller produces the CSV. */
  onDownload: (filtered: IeepaAnalysisEntry[], opts: ExportOptions) => void;
}

/**
 * "Is this entry CAPE Eligible?" — same predicate as the CAPE Eligible
 * tab on the detail page. Anything passing this is a candidate for a
 * brand-new Phase 1 filing: bucket window OK, not yet certified by CBP,
 * no claim yet on the broker side, and a measurable dollar exposure.
 *
 * `hasExistingProtest` and `reconciliationIndicator` are NOT filtered
 * here — those are toggleable add-ons / removers on the dialog itself.
 */
const isCapeEligible = (e: IeepaAnalysisEntry): boolean =>
  e.bucket === "CAPE" &&
  e.refundType !== "CAPE" &&
  !hasFiledClaim(e.claimStatus) &&
  refundableAmount(e) > 0;

/**
 * Same idea but for the Protest Recommended + Out of Window window —
 * entries that didn't make CAPE Phase 1 but still represent a candidate
 * filing (formal protest under 19 CFR § 174.12(e), or out-of-window
 * which the broker may submit anyway). Same exclusions as above.
 */
const isOutOfWindowEligible = (e: IeepaAnalysisEntry): boolean =>
  (e.bucket === "PROTEST_RECOMMENDED" || e.bucket === "OUT_OF_WINDOW") &&
  e.refundType !== "CAPE" &&
  !hasFiledClaim(e.claimStatus) &&
  refundableAmount(e) > 0;

/**
 * Pure filter — extracted so it can be unit-tested without React.
 *
 * Mental model: BASE scope is CAPE Eligible (mirrors the page tab of
 * the same name). Toggles are *additive* (protests, out-of-window)
 * except for `includeRecon` which is *subtractive* — recon-flagged
 * rows are part of the eligible set by default and the toggle removes
 * them when the broker excludes recon entries from filings.
 *
 * Critical correctness: `refundType === 'CAPE'` rows are excluded
 * unconditionally. CBP has already refunded them; resubmitting via
 * the Phase 1 portal would be a duplicate filing.
 */
export const applyExportFilter = (
  entries: IeepaAnalysisEntry[],
  opts: ExportOptions
): IeepaAnalysisEntry[] =>
  entries.filter((e) => {
    const inBaseScope =
      isCapeEligible(e) ||
      (opts.includeOutOfWindow && isOutOfWindowEligible(e));
    if (!inBaseScope) return false;
    if (!opts.includeProtests && e.hasExistingProtest) return false;
    if (!opts.includeRecon && e.reconciliationIndicator === "Y") return false;
    return true;
  });

/**
 * Three-question modal the user hits from the "Cape Report" button.
 * Each checkbox shows the *delta* a flip would make against the
 * currently-active scope, so the math always reconciles with the
 * "Will export" total at the bottom.
 */
export const ExportCapeReportDialog: FC<ExportCapeReportDialogProps> = ({
  open,
  onClose,
  entries,
  onDownload,
}) => {
  const [opts, setOpts] = useState<ExportOptions>({
    includeProtests: false,
    includeOutOfWindow: false,
    includeRecon: true,
  });

  // All counts are deltas against the currently-active scope, computed
  // by re-running `applyExportFilter` with one toggle flipped. The
  // user-facing rule is "if I flip this checkbox, this many rows move
  // in or out of the export." Adds up to the correct total every time.
  const counts = useMemo(() => {
    const base = applyExportFilter(entries, opts).length;

    const flippedProtest = applyExportFilter(entries, {
      ...opts,
      includeProtests: !opts.includeProtests,
    }).length;
    const flippedOutOfWindow = applyExportFilter(entries, {
      ...opts,
      includeOutOfWindow: !opts.includeOutOfWindow,
    }).length;
    const flippedRecon = applyExportFilter(entries, {
      ...opts,
      includeRecon: !opts.includeRecon,
    }).length;

    return {
      base,
      // CAPE Eligible base scope (always shown in the header).
      capeEligible: entries.filter(isCapeEligible).length,
      // Magnitude of the change each toggle would cause from the
      // current state. Always non-negative; shown as "± N entries".
      protestDelta: Math.abs(flippedProtest - base),
      outOfWindowDelta: Math.abs(flippedOutOfWindow - base),
      reconDelta: Math.abs(flippedRecon - base),
    };
  }, [entries, opts]);

  const filtered = useMemo(
    () => applyExportFilter(entries, opts),
    [entries, opts]
  );

  const handleDownload = () => {
    onDownload(filtered, opts);
    onClose();
  };

  const setOpt = (key: keyof ExportOptions, v: boolean) =>
    setOpts((s) => ({ ...s, [key]: v }));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          pb: 1,
          fontSize: tokens.fontSize.lg,
          fontWeight: tokens.fontWeight.semibold,
          color: tokens.text.primary,
        }}
      >
        Export Cape Report
      </DialogTitle>
      <DialogContent sx={{ pt: "0 !important" }}>
        <Typography
          sx={{
            fontSize: tokens.fontSize.sm2,
            color: tokens.text.muted,
            mb: 2,
          }}
        >
          Base scope is{" "}
          <strong>CAPE Eligible</strong> (
          {counts.capeEligible.toLocaleString()} entries) — no claim filed
          yet, IEEPA duty still on the entry. Pick what else to include.
        </Typography>

        <Stack spacing={1.5}>
          <CheckboxRow
            checked={opts.includeProtests}
            onChange={(v) => setOpt("includeProtests", v)}
            label="Include entries with existing protests"
            description={
              opts.includeProtests
                ? `Currently included. Turning this off removes ${counts.protestDelta.toLocaleString()} entries with a Protest Number on ACE.`
                : `${counts.protestDelta.toLocaleString()} entries in scope already have a Protest Number on ACE. Counsel normally handles them.`
            }
            count={counts.protestDelta}
            countTone={tokens.status.warning}
            countLabel={opts.includeProtests ? "currently in" : "would add"}
          />
          <CheckboxRow
            checked={opts.includeOutOfWindow}
            onChange={(v) => setOpt("includeOutOfWindow", v)}
            label="Include out-of-window entries"
            description={
              opts.includeOutOfWindow
                ? `Currently included. Turning this off removes ${counts.outOfWindowDelta.toLocaleString()} Protest Recommended + Out of Window entries.`
                : `${counts.outOfWindowDelta.toLocaleString()} Protest Recommended + Out of Window entries match the eligible criteria (no claim, IEEPA duty on entry, not certified).`
            }
            count={counts.outOfWindowDelta}
            countTone={tokens.status.danger}
            countLabel={opts.includeOutOfWindow ? "currently in" : "would add"}
          />
          <CheckboxRow
            checked={opts.includeRecon}
            onChange={(v) => setOpt("includeRecon", v)}
            label="Include recon-flagged entries"
            description={
              opts.includeRecon
                ? `${counts.reconDelta.toLocaleString()} Recon=Y entries are currently in scope. Recon does not disqualify Phase 1; turn off only if the broker excludes them.`
                : `${counts.reconDelta.toLocaleString()} Recon=Y entries are currently excluded. Most brokers include them.`
            }
            count={counts.reconDelta}
            countTone={tokens.text.muted}
            countLabel={opts.includeRecon ? "currently in" : "would add"}
          />
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Box
          sx={{
            p: 2,
            borderRadius: tokens.radius.md,
            bgcolor: tokens.bg.muted,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.muted }}>
            Will export
          </Typography>
          <Typography
            sx={{
              fontSize: tokens.fontSize.lg,
              fontWeight: tokens.fontWeight.semibold,
              color: tokens.brand.primary,
            }}
          >
            {filtered.length.toLocaleString()} entries
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<Download />}
          onClick={handleDownload}
          disabled={filtered.length === 0}
          sx={{
            textTransform: "none",
            bgcolor: tokens.brand.primary,
            "&:hover": { bgcolor: tokens.brand.primaryHover },
          }}
        >
          Download CSV
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface CheckboxRowProps {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  description: string;
  count: number;
  countTone: string;
  /** Short label next to the count, e.g. "would add" or "currently in". */
  countLabel: string;
}

const CheckboxRow: FC<CheckboxRowProps> = ({
  checked,
  onChange,
  label,
  description,
  count,
  countTone,
  countLabel,
}) => (
  <FormControlLabel
    control={
      <Checkbox
        checked={checked}
        onChange={(_, v) => onChange(v)}
        sx={{ alignSelf: "flex-start", pt: 0 }}
      />
    }
    label={
      <Box sx={{ pl: 0.5 }}>
        <Stack direction="row" spacing={1} alignItems="baseline">
          <Typography
            sx={{
              fontSize: tokens.fontSize.md,
              fontWeight: tokens.fontWeight.semibold,
              color: tokens.text.primary,
            }}
          >
            {label}
          </Typography>
          <Typography
            sx={{
              fontSize: "10px",
              fontWeight: tokens.fontWeight.semibold,
              color: countTone,
              bgcolor: `${countTone}15`,
              px: 0.75,
              py: 0.15,
              borderRadius: "3px",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            {countLabel} {count.toLocaleString()}
          </Typography>
        </Stack>
        <Typography
          sx={{
            fontSize: tokens.fontSize.sm2,
            color: tokens.text.muted,
            mt: 0.25,
          }}
        >
          {description}
        </Typography>
      </Box>
    }
    sx={{ alignItems: "flex-start", m: 0 }}
  />
);
